import { createContext, useContext, useState, useEffect } from 'react';
import { API, SIGNED_IN_KEY, legacyToken } from '../utils/api';

const WorkspaceContext = createContext(null);

const DRIVE_KEY  = 'gos_drive_mode';
const OWNER_KEY  = 'gos_ws_owner';

export function WorkspaceProvider({ children }) {
  const [driveMode, setDriveMode] = useState(() => localStorage.getItem(DRIVE_KEY) || 'personal');
  const [wsOwner,   setWsOwner]   = useState(() => localStorage.getItem(OWNER_KEY) || null);
  const [memberOf,  setMemberOf]  = useState([]);
  const [role,      setRole]      = useState('owner');
  const [canEdit,   setCanEdit]   = useState(!localStorage.getItem(OWNER_KEY));
  const [isLoaded,  setIsLoaded]  = useState(false);

  useEffect(() => {
    // Signed in = the cookie session's flag, or a token from the pre-cookie app.
    const token = legacyToken();
    if (!token && !localStorage.getItem(SIGNED_IN_KEY)) { setIsLoaded(true); return; }

    const headers = { 'X-Requested-With': 'XMLHttpRequest' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (wsOwner) headers['X-Workspace-Owner'] = wsOwner;

    Promise.all([
      fetch(`${API}/workspace/list`, { headers, credentials: 'include' })
        .then(r => r.ok ? r.json() : { member: [] })
        .catch(() => ({ member: [] })),
      fetch(`${API}/workspace/my-permissions`, { headers, credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .catch(() => null),
    ]).then(([listData, permsData]) => {
      setMemberOf(listData.member || []);
      if (permsData) {
        setRole(permsData.role || 'owner');
        setCanEdit(permsData.canEdit !== false);
      }
      setIsLoaded(true);
    }).catch(() => setIsLoaded(true));
  }, [wsOwner]);

  const switchToPersonal = () => {
    localStorage.setItem(DRIVE_KEY, 'personal');
    localStorage.removeItem(OWNER_KEY);
    setDriveMode('personal');
    setWsOwner(null);
    setRole('owner');
    setCanEdit(true);
  };

  const switchToTeam = (ownerEmail) => {
    localStorage.setItem(DRIVE_KEY, 'team');
    localStorage.setItem(OWNER_KEY, ownerEmail);
    setDriveMode('team');
    setWsOwner(ownerEmail);
    setRole(null);
    setCanEdit(false); // conservative default until permissions confirmed
  };

  return (
    <WorkspaceContext.Provider value={{
      driveMode, wsOwner, memberOf, role, canEdit, isLoaded,
      switchToPersonal, switchToTeam,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => useContext(WorkspaceContext);
